import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import {
  Button,
  Header,
  Icon,
  Modal,
  Grid,
  Loader,
  Dimmer,
  Radio
} from 'semantic-ui-react';
import { Field, Formik } from 'formik';
import { FieldLabel } from '../../../../../styles';
import * as S from './styles';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../../services/notification';
import { fileTypeProps } from '../../../../../components/UploadFilesUncontroled';
import CustomDatePicker from '../../../../../components/CustomDatePicker';
import AppMaskedInput from '../../../../../components/MaskedInput';
import { DATE_INPUT_MASK } from '../../../../../services/masks';
import {
  getFilesLinkAxios,
  postFileAndGetPreSignAxios
} from '../../../../../services/file';
import { put } from '../../../../../services/request';

type VacatePropertyClaimModalType = {
  claimId: string;
  insurerName: string;
  fileIdentifiers: fileTypeProps[];
  onClose: () => void;
  callback: () => void;
};

const VacatePropertyClaimModal = ({
  claimId,
  insurerName,
  fileIdentifiers,
  onClose,
  callback
}: VacatePropertyClaimModalType) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [activeUploadKey, setActiveUploadKey] = useState<string | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [existingFiles, setExistingFiles] = useState<any[]>([]);
  const initialValues = {
    claimEstate: {
      id: claimId
    },
    claimProperty: {
      deliveryKeysDate: ''
    },
    claimFiles: [],
    damageControl: false
  };

  const getFiles = async () => {
    const files = await getFilesLinkAxios(claimId, 'CLAIM_ESTATES');
    setExistingFiles(files?.urls || []);
  };

  const handleUploadClick = (key: string) => {
    setActiveUploadKey(key);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const orderedFiles = (files: any[]) => {
    return files.sort((a, b) => {
      if (a.required !== b.required) {
        return a.required ? -1 : 1;
      }
      return a.name.localeCompare(b.name, 'pt-BR', {
        sensitivity: 'base'
      });
    });
  };

  async function submitFiles(
    files: {
      file: File;
      name: string;
    }[]
  ) {
    const responses = [];
    for (const file of files) {
      const response = await postFileAndGetPreSignAxios({
        filename: file.file.name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[`~!@#$%^&*()_|+-=?;:'",<>{}[]\/]/gi, ''),
        contentType: file.file.type,
        referencedTableName: 'CLAIM_ESTATES',
        referencedTableId: claimId,
        name: file.name
      });

      const fileToBlob = async (theFile: File) =>
        new Blob([new Uint8Array(await theFile.arrayBuffer())], {
          type: theFile.type
        });
      const blob = await fileToBlob(file.file);

      await fetch(response.uploadURL, {
        method: 'PUT',
        body: blob
      });
      responses.push({ id: response.id, contextType: file.name });
    }
    return responses;
  }

  const submitValues = async (values: any) => {
    if (!values.claimProperty.deliveryKeysDate) {
      warningNotification(
        'Data de entrega das chaves é obrigatória',
        'Por favor, preencha a data de entrega das chaves antes de continuar.'
      );
      return;
    }

    const filesRequired = fileIdentifiers.filter(f => f.required).map(f => f.key);

    existingFiles.forEach(f => {
      const index = filesRequired.indexOf(f.name);
      if (index > -1) {
        filesRequired.splice(index, 1);
      }
    });

    values.claimFiles.forEach((f: any) => {
      const index = filesRequired.indexOf(f.name);
      if (index > -1) {
        filesRequired.splice(index, 1);
      }
    });

    if (filesRequired.length > 0) {
      warningNotification(
        'Arquivos obrigatórios não enviados',
        'Por favor, envie todos os arquivos obrigatórios antes de continuar.'
      );
      return;
    }

    try {
      setLoading(true);
      await put(`/claims/vacate-property/${claimId}`, values);
      await submitFiles(values.claimFiles);
      successNotification('Sucesso!', 'Imóvel desocupado com sucesso.');
      onClose();
      setLoading(false);
      callback();
    } catch (error) {
      setLoading(false);
      dangerNotification(
        'Erro!',
        'Erro ao desocupar o imóvel. Tente novamente mais tarde.'
      ); // Erro desconhecido
      return;
    }
  };

  useEffect(() => {
    const sorted = orderedFiles(fileIdentifiers);
    setDocuments(sorted);
  }, [fileIdentifiers]);

  useEffect(() => {
    getFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Modal style={{ zIndex: 10 }} size="large" closeIcon open={true} onClose={onClose}>
        <Formik
          initialValues={initialValues!}
          validationSchema={() => true}
          onSubmit={e => submitValues(e)}
        >
          {innerValues => {
            const { values, setFieldValue } = innerValues;

            const getFilesByKey = (key: string) => {
              return values.claimFiles.filter((f: any) => f.name === key);
            };

            const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const files = e.target.files;
              if (files && files.length > 0 && activeUploadKey) {
                const file = files[0];
                if (file.size > 50 * 1024 * 1024) {
                  warningNotification(
                    'Arquivo muito grande',
                    'O arquivo excede o limite de 50MB.'
                  );
                  return;
                }

                const newFileEntry = { file, name: activeUploadKey };
                setFieldValue('claimFiles', [...values.claimFiles, newFileEntry]);
              }
              setActiveUploadKey(null);
            };

            const handleDeleteClick = (key: string, indexToDelete: number) => {
              if (window.confirm('Tem certeza que deseja remover este arquivo?')) {
                const filesForKey = values.claimFiles.filter((f: any) => f.name === key);
                const otherFiles = values.claimFiles.filter((f: any) => f.name !== key);
                const newFilesForKey = filesForKey.filter(
                  (_: any, index: number) => index !== indexToDelete
                );
                setFieldValue('claimFiles', [...otherFiles, ...newFilesForKey]);
              }
            };

            const handleDamageControlChange = (value: boolean) => {
              setFieldValue('damageControl', value);
              const newDocuments = documents.map(doc => {
                if (
                  doc.key === 'BUDGETS' ||
                  doc.key === 'FINAL_INSPECTION_PROPERTY' ||
                  doc.key === 'INITIAL_INSPECTION_PROPERTY'
                ) {
                  doc.required = value;
                  return doc;
                }
                return doc;
              });
              const orderedNewDocuments = orderedFiles(newDocuments);
              setDocuments(orderedNewDocuments);
            };

            return (
              <>
                <Dimmer active={loading}>
                  <Loader indeterminate>
                    Salvando informações, por favor aguarde...
                  </Loader>
                </Dimmer>
                <Header style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex' }}>
                    <Icon name={'file alternate outline'} /> Desocupar imóvel
                  </div>
                </Header>
                <Modal.Content scrolling>
                  <Grid columns={16}>
                    <Grid.Row>
                      <Grid.Column width={4}>
                        <FieldLabel>Status de ocupação do imóvel</FieldLabel>
                        Desocupado
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <FieldLabel>Seguradora/Garantidora</FieldLabel>
                        {insurerName || 'Nenhuma seguradora'}
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid.Column width={4}>
                        <FieldLabel required>Data de entrega das chaves</FieldLabel>
                        <Field
                          name={`claimProperty.deliveryKeysDate`}
                          selected={
                            values.claimProperty.deliveryKeysDate
                              ? new Date(values.claimProperty.deliveryKeysDate)
                              : undefined
                          }
                          component={CustomDatePicker}
                          customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
                          dateFormat={'dd/MM/yyyy'}
                          onChange={(date: Date, _e: SyntheticEvent) => {
                            if (!date) {
                              setFieldValue('claimProperty.deliveryKeysDate', '');
                              return;
                            }
                            setFieldValue(
                              'claimProperty.deliveryKeysDate',
                              date
                              // FormatDateYMD_UTC_IGNORE(date)
                            );
                          }}
                        />
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <FieldLabel required>
                          Será reclamado danos ao imóvel ou a pintura?
                        </FieldLabel>
                        <div style={{ paddingTop: '1rem', display: 'flex', gap: '1rem' }}>
                          <Radio
                            label="Sim"
                            name="damageControl"
                            checked={values.damageControl === true}
                            onChange={() => handleDamageControlChange(true)}
                          />
                          <Radio
                            label="Não"
                            name="damageControl"
                            checked={values.damageControl === false}
                            onChange={() => handleDamageControlChange(false)}
                          />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>

                  <S.DocumentsListContainer>
                    <S.ListHeader>
                      <S.ListHeaderLabel className="col-name">
                        <Icon name="fire" style={{ color: '#59249B' }} />
                        Arquivos necessários
                      </S.ListHeaderLabel>
                      <S.ListHeaderLabel className="col-status">Status</S.ListHeaderLabel>
                      <S.ListHeaderLabel className="col-actions">Ações</S.ListHeaderLabel>
                    </S.ListHeader>

                    {documents.map(item => {
                      const uploadedFiles = getFilesByKey(item.key);
                      const currentExistingFiles =
                        existingFiles?.filter(f => f.name === item.key) || [];
                      const hasFiles =
                        uploadedFiles.length > 0 || currentExistingFiles.length > 0;
                      const isRequired = item.required === true;

                      return (
                        <React.Fragment key={item.key}>
                          {currentExistingFiles.map((existingFile, index) => (
                            <S.ListItem key={`existing-${existingFile.id}`}>
                              <S.DocumentName>
                                <S.DocumentIconWrapper>
                                  <Icon
                                    name="file text outline"
                                    style={{ color: '#59249B' }}
                                  />
                                </S.DocumentIconWrapper>
                                <a
                                  href={existingFile.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  {item.name} (Salvo - {index + 1})
                                </a>
                                {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                              </S.DocumentName>
                              <S.StatusWrapper>
                                <S.StatusBadge status="sent">
                                  <S.StatusDot status="sent" />
                                  <S.StatusText>Salvo</S.StatusText>
                                </S.StatusBadge>
                              </S.StatusWrapper>
                              <S.ActionsWrapper></S.ActionsWrapper>
                            </S.ListItem>
                          ))}

                          {uploadedFiles.map((uploadedFile: any, index: number) => (
                            <S.ListItem key={`${item.key}-${index}`}>
                              <S.DocumentName>
                                <S.DocumentIconWrapper>
                                  <Icon
                                    name="file text outline"
                                    style={{ color: '#59249B' }}
                                  />
                                </S.DocumentIconWrapper>
                                {item.name} ({index + 1})
                                {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                              </S.DocumentName>
                              <S.StatusWrapper>
                                <S.StatusBadge status="sent">
                                  <S.StatusDot status="sent" />
                                  <S.StatusText>Enviado</S.StatusText>
                                </S.StatusBadge>
                              </S.StatusWrapper>
                              <S.ActionsWrapper>
                                <S.ActionButton
                                  actionType="delete"
                                  onClick={() => handleDeleteClick(item.key, index)}
                                  title="Remover arquivo"
                                >
                                  <Icon name="times" style={{ margin: 0 }} />
                                </S.ActionButton>
                              </S.ActionsWrapper>
                            </S.ListItem>
                          ))}

                          <S.ListItem key={`${item.key}-add`}>
                            <S.DocumentName>
                              <S.DocumentIconWrapper>
                                <Icon
                                  name="file text outline"
                                  style={{
                                    color: hasFiles
                                      ? '#59249B'
                                      : isRequired
                                      ? '#FF3B30'
                                      : '#999'
                                  }}
                                />
                              </S.DocumentIconWrapper>
                              {item.name} {hasFiles ? '(Adicionar Novo)' : ''}
                              {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                            </S.DocumentName>
                            <S.StatusWrapper>
                              {!hasFiles && (
                                <S.StatusBadge status="pending">
                                  <S.StatusDot status="pending" />
                                  <S.StatusText>
                                    {isRequired ? 'Obrigatório' : 'Pendente'}
                                  </S.StatusText>
                                </S.StatusBadge>
                              )}
                            </S.StatusWrapper>
                            <S.ActionsWrapper>
                              <S.ActionButton
                                actionType="upload"
                                onClick={() => handleUploadClick(item.key)}
                                title="Enviar arquivo"
                              >
                                <Icon name="upload" style={{ margin: 0 }} />
                              </S.ActionButton>
                            </S.ActionsWrapper>
                          </S.ListItem>
                        </React.Fragment>
                      );
                    })}
                  </S.DocumentsListContainer>

                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".jpeg,.jpg,.png,.webp,.doc,.docx,.pdf,.odt"
                  />
                </Modal.Content>
                <Modal.Actions>
                  <Button color="red" onClick={() => onClose()}>
                    <Icon name="remove" />
                    Cancelar
                  </Button>
                  <Button
                    color="green"
                    type="submit"
                    onClick={() => innerValues.handleSubmit()}
                  >
                    <Icon name="checkmark" /> Salvar
                  </Button>
                </Modal.Actions>
              </>
            );
          }}
        </Formik>
      </Modal>
    </div>
  );
};

export default VacatePropertyClaimModal;
